import { AllMonths, Month } from './Month';
import { Validation, ValidationResult } from './Validation';
//import PriceUtils from './PriceUtils';
import { AllWeekDays, WeekDay } from './WeekDay';
import CategoryTypeJson, { validateCategoryTypeJson } from './CategoryTypeJson';

/**
 * Verleihbediung, gibt einen festen Preis für einen festen Verleihzeitraum an.
 */
export default interface RentalConditionJson {
    ID: number | null,
    
    /**
    * Name des Verleihtarifs.
    */
    title: string;

    /**
    * Besondere Hinweise, die für diesen Verleihtarif gelten. Kann ein leerer String sein wenn nichts besonderes gilt.
    */
    remarks: string;

    /**
    * Mietpreis in Eurocent. Bruttogesamtpreis für den gesamten Mietzeitraum.
    */
    price: number,

    /**
     * Gibt an, wie welcher Anteil an Mehrwertsteuer in dem obigen Preis enthalten ist.
     */
    priceIncludedVATRate: number;

    /**
     * Ist ein Preis auf Anfrage, ist das auf true gesetzt. Dann soll das Frontend bitte den obigen "price" nicht anzeigen. Er existiert aber trotzdem schon, damit die Verwaltung was vorausgefüllt haben kann.
     */
    isOnRequestOnly: boolean;

    /**
     * Wenn true, kann der Preis nur von der Verwaltung ausgewählt werden.
     */
    isManagementOnly: boolean;

    /**
     * Wenn true, wird der Preis nur für die Verwaltung angezeigt.
     */
    showManagementOnly: boolean;

    /**
     * Preis wird als Pauschalpreis für den gesamten Mietzeitraum gerechnet (nicht pro Tag).
     */
    ignoreRentalPeriod: boolean,

    /**
     * Preis wird als Pauschalpreis für die gesamte Anzahl gerechnet (nicht mit Anzahl multipliziert).
     */
    ignoreRentalAmount: boolean,

    /**
     * Kosten die bei einer Stornierung anfallen in Eurocent.
     */
    cancellationFee: number;

    /**
     * Stornierungsbedingungen.
     */
    cancellationRemarks: string;

    /** 
     * Gibt an, ab welchen Tag der Zeitraum für eine Buchung zu diesen Konditionen zählt (z.B. Donnerstag mit 4 Tage Zeitraum für Wochenendbuchungen). Bei null sind alle Wochentage möglich.
     */
    rentalDurationStartWeekDay: WeekDay | null;

    /**
     * Gibt an, für wie viele Tage zu diesen Bedingungen gemietet wird.
     */
    rentalDurationDays: number;

    /**
     * Gibt an für welche Typen von Kunden diese Bedingungen gelten. Wenn der type null ist, kann jeder (auch ohne Account) diese Bedingungen buchen
     */
    restrictToTypes: CategoryTypeJson[] | null;

    /**
     * Gibt an, ob es eine maximale Buchungsdauer in Tagen gibt. Nur Relevant für Leihbedingungen die wiederholbar sind (Tages oder Wochenverleih).
     */
    restrictMaxRentalDurationDays: number | null;

    /** 
     * Gibt an, in welchen Monaten eine Buchung zu diesen Konditionen starten kann. Ermöglicht Verleih auf gewisse Monate zu begrenzen
     */
    rentalStartMonths: Month[];
}

export function defaultRentalConditionJson(): RentalConditionJson {
    return {
        ID: null,
        title: "",
        remarks: "",
        price: 0,
        priceIncludedVATRate: 0,
        isOnRequestOnly: false,
        isManagementOnly: false,
        showManagementOnly: false,
        ignoreRentalPeriod: false,
        ignoreRentalAmount: false,
        cancellationFee: 0,
        cancellationRemarks: "",
        rentalDurationStartWeekDay: null,
        rentalDurationDays: 1,
        rentalStartMonths: AllMonths,
        restrictToTypes: null,
        restrictMaxRentalDurationDays: null,
    };
}

export function validateRentalConditionJsonAdd(json: RentalConditionJson): ValidationResult<RentalConditionJson> {
    return new Validation(json)
        .validateEquals("ID", null)
        .validateNonEmptyString("title", 100)
        .validateString("remarks", 500)
        .validateInteger("price", 0, null, false)
        .validateNumber("priceIncludedVATRate", 0, 1)
        .validateInteger("cancellationFee", 0, null, false)
        .validateString("cancellationRemarks", 500)
        .validateInteger("rentalDurationDays", 0, 90, false)
        .validateAllUsingFunction("restrictToTypes", validateCategoryTypeJson, 0, true)
        .validateInteger("restrictMaxRentalDurationDays", 0, 90, true)
        .finish();
}

export function validateRentalConditionJson(json: RentalConditionJson): ValidationResult<RentalConditionJson> {
    return new Validation(json)
        .validateInteger("ID", 0, null, true)
        .validateNonEmptyString("title", 100)
        .validateString("remarks", 500)
        .validateInteger("price", 0, null, false)
        .validateNumber("priceIncludedVATRate", 0, 1)
        .validateInteger("cancellationFee", 0, null, false)
        .validateString("cancellationRemarks", 500)
        .validateInteger("rentalDurationDays", 0, 90, false)
        .validateAllUsingFunction("restrictToTypes", validateCategoryTypeJson, 0, true)
        .validateInteger("restrictMaxRentalDurationDays", 0, 90, true)
        .finish();
}

export function validateUniqueRentalConditionTitle(title: string, rentalConditions: RentalConditionJson[]) {
    let titleExists = rentalConditions.filter(i => i.title == title);

    return titleExists.length <= 1 ? true : false;
}

export function getRentalConditionPrice(rentalCondition: RentalConditionJson, amount: number, pickupDate: Date, returnDate: Date): number {
    var conditionPrice = rentalCondition.price * getRentalConditionPaymentRepeatCount(rentalCondition, pickupDate, returnDate);

    if(!rentalCondition.ignoreRentalAmount) {
        conditionPrice = conditionPrice * amount;
    }
    
    return conditionPrice;
}

/* export function getRentalAdditionalConditionPrice(rentalCondition: RentalConditionJson): number {
    return rentalCondition.price;
} */

function getRentalConditionPaymentRepeatCount(rentalCondition: RentalConditionJson, pickupDate: Date, returnDate: Date): number {
    var repetitions = 1;

    if(!rentalCondition.ignoreRentalPeriod) {
        let currentDate = new Date(pickupDate);

        let getValidStartDate = function (date: Date) {
            let startDate = new Date(date);
            while (rentalCondition.rentalDurationStartWeekDay && startDate.getDay() != rentalCondition.rentalDurationStartWeekDay) {
                startDate.setDate(startDate.getDate() - 1);
            }
            return startDate;
        };

        let rentalStartDate = getValidStartDate(currentDate);
        var rentalEndDate = new Date(rentalStartDate);
        rentalEndDate.setDate(rentalEndDate.getDate() + rentalCondition.rentalDurationDays);

        // Zur Berechnung pro Tag (nicht pro Nacht) wird eine Wiederholung addiert
        if(currentDate.getTime() !== returnDate.getTime() && rentalCondition.rentalDurationDays <= 1) {
            repetitions += 1;
        }

        while (currentDate <= returnDate) {
            if (currentDate > rentalEndDate) {
                repetitions += 1;
                rentalEndDate.setDate(rentalEndDate.getDate() + rentalCondition.rentalDurationDays);
            }

            currentDate.setDate(currentDate.getDate() + 1);
        }
    }

    return repetitions;
}

/* export function getRentalConditionWithChangedVATRate(rentalCondition: RentalConditionJson, newVATRate: number): RentalConditionJson {
    let newRentalCondition = JSON.parse(JSON.stringify(rentalCondition));
    newRentalCondition.price = PriceUtils.changePriceVATRate(rentalCondition.price, rentalCondition.priceIncludedVATRate, newVATRate);
    newRentalCondition.priceIncludedVATRate = newVATRate;
    return newRentalCondition;
} */